import React, { useEffect, useState } from 'react'
import { PrismicRichText } from '@prismicio/react'
import { OverflowDetector } from 'react-overflow'
import { get } from 'libraries/lodash'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import withStyles from '@material-ui/core/styles/withStyles'
import MuiIconButton from '@material-ui/core/IconButton'
import MuiExpandMoreRounded from '@material-ui/icons/ExpandMoreRounded'
import MuiExpandLessRounded from '@material-ui/icons/ExpandLessRounded'
import TealToolTip from 'components/TealToolTip'
import cx from 'classnames'
import { useSelector } from 'react-redux'
import selectIsTouchDevice from 'redux/selectors/selectIsTouchDevice'
import { WidthResized } from 'services/WindowService'
import { skip } from 'rxjs/operators'

const IconButton = withStyles({
  root: {
    color: 'white',
    padding: 8
  }
})(MuiIconButton)

const DesktopExpandMoreRounded = withStyles({
  colorPrimary: {
    color: 'white'
  }
})(MuiExpandMoreRounded)

const DesktopExpandLessRounded = withStyles({
  colorPrimary: {
    color: 'white'
  }
})(MuiExpandLessRounded)

const ShowMoreIcon = ({ expanded }) => expanded ? <DesktopExpandLessRounded /> : <DesktopExpandMoreRounded />

const ExpandMoreButton = ({ handleOnClick, expanded, isOverFlowed }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [showToolTip, setShowToolTip] = useState(false)
  const isTouchDevice = useSelector(selectIsTouchDevice)
  const [showButton, setShowButton] = useState(isOverFlowed || expanded)

  // prevent tooltips from showing immediately after an onClick event
  useEffect(() => {
    setShowToolTip(false)
    if (tooltipOpen) {
      const timer = setTimeout(() => setShowToolTip(true), 300)
      return () => clearTimeout(timer)
    }
  }, [tooltipOpen])

  useEffect(() => {
    const subscriber = WidthResized.pipe(
      skip(1)
    ).subscribe((x) => {
      setShowButton(isOverFlowed || expanded)
    })

    return () => {
      subscriber.unsubscribe()
    }
  }, [isOverFlowed, expanded, setShowButton])

  // required because isOverflowed isn't detected on initial render
  // We can't use isOverflowed or isOverflowed || expanded directly due to the interaction it has when
  // the panels are minimised where for a brief time expanded is false
  // but the panel is not yet overflowed
  useEffect(() => {
    if (isOverFlowed) {
      setShowButton(true)
    }
  }, [isOverFlowed])

  if (!showButton) {
    return null
  }

  return (
    <div className='expand-more-button-container w-100 h2 tc absolute bottom0'>
      <TealToolTip open={showToolTip} title={expanded ? 'Show less' : 'Show more'} placement='right'>
        <IconButton
          aria-label='expand button'
          style={{
            color: tooltipOpen ? '#00719F' : '#232A3A'
          }}
          classes={{
            root: cx(
              'mt2 outline-0 o-80',
              { 'o-100': tooltipOpen },
              { 'icon-button-bg-color-active': isTouchDevice || expanded }
            )
          }}
          onClick={handleOnClick}
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
          onMouseDown={() => setTooltipOpen(false)}
        >
          <ShowMoreIcon expanded={expanded} />
        </IconButton>
      </TealToolTip>
    </div>
  )
}

const ContentPanel = ({ panelTitle, panelContent, height }) => {
  const [expanded, setExpanded] = useState(false)
  const [heightOverflowing, setHeightOverflowing] = useState(false)
  const handleShowMoreButtonOnClick = () => setExpanded(expanded => !expanded)
  const handleOverflowChange = (isHeightOverflowed) => setHeightOverflowing(isHeightOverflowed)
  const title = get(panelTitle, '[0].text')

  return (
    <div className='bg-twilight-grey relative' style={{ minHeight: height }} data-testid='content-panel'>
      <OverflowDetector
        className='overflow-detector'
        onOverflowChange={handleOverflowChange}
        style={{
          maxHeight: expanded ? 2000 : height,
          transition: expanded ? 'max-height 0.5s ease-in' : 'max-height 0.6s ease-out',
          transitionDelay: expanded ? '0.25s' : '0.0s',
          overflow: 'hidden'
        }}
      >
        <h2 className='ma0 ph6 pt4 black fw6 f4 lh-headline'>
          {title}
        </h2>
        <div className='announcements-content ph6 pb6 pt2 f4' style={{ overflow: 'hidden' }}>
          <PrismicRichText field={panelContent} />
        </div>
      </OverflowDetector>
      {expanded ? <div className='h2' /> : null}
      <ExpandMoreButton
        isOverFlowed={heightOverflowing}
        expanded={expanded}
        handleOnClick={handleShowMoreButtonOnClick}
      />
    </div>
  )
}

const NoResultPanel = ({
  panel_title: [{ text: 'No Updates' }],
  panel_content: null
})

const ContentPanels = ({ CMS_DATA, setHomePageFcUpdatesIsLoaded }) => {
  const panels = get(CMS_DATA, 'results[0].data.panel', [NoResultPanel])
  const totalHeight = 1728 - (16 * (panels.length - 1))
  const height = totalHeight / panels.length

  useEffect(() => {
    setHomePageFcUpdatesIsLoaded(true)
  }, [])

  return (
    <Grid container className='bg-ghost-grey h-100' spacing={2}>
      {panels.map((panel, idx) => (
        <Grid item xs={12} sm={6} md={6} lg={12} key={idx}>
          <Hidden mdDown>
            <ContentPanel panelTitle={panel.panel_title} panelContent={panel.panel_content} {...{ height }} />
          </Hidden>
          <Hidden lgUp>
            <ContentPanel panelTitle={panel.panel_title} panelContent={panel.panel_content} {...{ height: 595 }} />
          </Hidden>
        </Grid>
      ))}
    </Grid>
  )
}

export default ContentPanels
